import "./DeviceManagement.scss";

import clsx from "clsx";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet } from "react-router";
import { FCC } from "types";

import { TabLayout } from "@/components/Layout/BaseLayouts/TabLayout";
import { Header } from "@/components/Portal/DeviceManagement/Header/Header.tsx";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { isSimDetailsSectionActiveAtom } from "@/utils/atoms";
import { canOnlyBookData } from "@/utils/customerUtils";
import {
  isEsimchangeInProgress,
  isEsimresetInProgress,
} from "@/utils/deviceUtils";

import { DeviceManagementTabs } from "./components/DeviceManagementTabs";
import { resolveDeviceManagementRedirect } from "./deviceManagementRedirect";
import {
  computeDisabledHints,
  computeTabDisabledState,
  deriveSimState,
} from "./deviceManagementTabs.ts";
import {
  MOBILE_STANDALONE_EDIT_SUBPATHS,
  SUBSCRIPTION_CHANGE_FLOW_SUBPATHS,
  useDeviceRouteState,
  WATCH_SIM_CHECKOUT_SUBPATH,
} from "./hooks/useDeviceRouteState";
import { useManagedSim } from "./hooks/useManagedSim";
import { useManagedSimSideData } from "./hooks/useManagedSimSideData";
import { useResetSimEditState } from "./hooks/useResetSimEditState";

/**
 * The device management screen: header, tab strip and the routed sub-view for
 * the SIM addressed by the `simId` query param. This component only wires the
 * pieces together — data loading lives in the `hooks/` folder, the tab-disable
 * and redirect branching in the sibling pure modules, and the tab strip and
 * expiry banner in `components/`.
 */
export const DeviceManagement: FCC = () => {
  const [simId] = useSimIdFromQueryParam();
  const { t } = useTranslation("portal");
  const isMobile = useIsMobile();

  const route = useDeviceRouteState();
  const { data: simData } = useManagedSim(simId);
  const { customerData, esimInfo } = useManagedSimSideData(simId);
  const isEditingSimDetails = useResetSimEditState(simId);
  const isSimDetailsSectionActive = useAtomValue(isSimDetailsSectionActiveAtom);
  const needsIdCheck = useNeedsIDCheck();

  if (!simData) {
    return null;
  }

  const simState = deriveSimState(simData);
  const isModifyingESim =
    isEsimchangeInProgress(esimInfo) || isEsimresetInProgress(esimInfo);

  const redirectTarget = resolveDeviceManagementRedirect({
    simId,
    simStatus: simData.simStatus,
    needsIdCheck,
    isModifyingESim,
    managementSubPath: route.managementSubPath,
    isNumberPorting: simState.isNumberPorting,
  });
  if (redirectTarget) {
    return <Navigate to={redirectTarget} replace={true} />;
  }

  // The booking flows take over the whole view (no device header, no tab
  // strip) per the redesign.
  const standaloneSubPaths = [
    WATCH_SIM_CHECKOUT_SUBPATH,
    "roaming/booking",
    "roaming/checkout",
    ...SUBSCRIPTION_CHANGE_FLOW_SUBPATHS,
  ];
  if (
    route.managementSubPath &&
    standaloneSubPaths.includes(route.managementSubPath)
  ) {
    return (
      <>
        <div className="device-management-view">
          <div className="tablet:mx-4">
            <Outlet />
          </div>
        </div>
      </>
    );
  }

  const canBookVoiceOption = !(customerData && canOnlyBookData(customerData));
  const tabState = computeTabDisabledState(
    {
      ...route,
      isModifyingESim,
      isEditingSimDetails,
      isSimDetailsSectionActive,
    },
    simState,
  );
  const hints = computeDisabledHints(simData, simState, t);

  // On mobile, inline edits and pause/reactivate screens pin their card to
  // the viewport (portal header/bottom nav hidden) — also hide the device
  // header and tab strip in place so <Outlet /> never remounts (local section
  // state in Details.tsx would otherwise reset on click — same pattern as
  // Documents).
  const hideChromeOnMobile =
    isMobile &&
    (isEditingSimDetails ||
      isSimDetailsSectionActive ||
      route.isChangingVoiceSettings ||
      (!!route.managementSubPath &&
        (MOBILE_STANDALONE_EDIT_SUBPATHS as readonly string[]).includes(
          route.managementSubPath,
        )));

  return (
    <TabLayout
      className="device-management-view"
      hideChrome={hideChromeOnMobile}
      contentClassName={clsx(
        hideChromeOnMobile && "device-management-view--standalone-edit",
      )}
      header={<Header simDetail={simData} />}
      tabs={
        <DeviceManagementTabs
          simId={simId}
          ariaLabel={simData.alias ?? ""}
          simStatus={simData.simStatus}
          tabState={tabState}
          hints={hints}
          isFlatHome={simState.isFlatHome}
          isDataOnly={simState.isDataOnly}
          canBookVoiceOption={canBookVoiceOption}
        />
      }
    >
      <div id="device-management-container">
        <Outlet />
      </div>
    </TabLayout>
  );
};
